import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, Field, Toggle, Segmented, StringListEditor, inputCls } from './ui';

// WCAG göreli parlaklık: beyaz metin / ana renk kontrastı
function contrastWithWhite(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const lin = [0, 2, 4].map((i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  return 1.05 / (L + 0.05);
}

const num = (v) => (v === '' ? 0 : Math.max(0, Math.min(200, parseInt(v, 10) || 0)));

function UrlWithThumb({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input className={inputCls} value={value} placeholder="https://..." onChange={(e) => onChange(e.target.value)} />
        <div className="w-10 h-10 shrink-0 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center">
          {value ? <img src={value} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} onLoad={(e) => { e.currentTarget.style.visibility = 'visible'; }} /> : null}
        </div>
      </div>
    </Field>
  );
}

export function AppearanceTab({ config, update }) {
  const a = config.appearance;
  const set = (k, v) => update('appearance', { ...a, [k]: v });
  const ratio = contrastWithWhite(a.primaryColor);
  return (
    <>
      <Card title="Renk ve konum">
        <Field label="Ana renk">
          <div className="flex items-center gap-2">
            <input type="color" className="h-9 w-12 rounded border border-gray-300 p-0.5" value={/^#[0-9a-f]{6}$/i.test(a.primaryColor) ? a.primaryColor : '#6366f1'} onChange={(e) => set('primaryColor', e.target.value)} />
            <input className={`${inputCls} max-w-[140px]`} value={a.primaryColor} onChange={(e) => set('primaryColor', e.target.value)} />
          </div>
          {ratio !== null && ratio < 4.5 && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Beyaz metin bu renkte okunaksız olabilir (kontrast {ratio.toFixed(2)}:1, en az 4.5:1 olmalı). Daha koyu bir renk seç.
            </div>
          )}
        </Field>
        <Field label="Konum"><Segmented value={a.position} onChange={(v) => set('position', v)} options={[{ value: 'right', label: 'Sağ' }, { value: 'left', label: 'Sol' }]} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Yatay boşluk (px)"><input type="number" min={0} max={200} className={inputCls} value={a.offsetX} onChange={(e) => set('offsetX', num(e.target.value))} /></Field>
          <Field label="Dikey boşluk (px)"><input type="number" min={0} max={200} className={inputCls} value={a.offsetY} onChange={(e) => set('offsetY', num(e.target.value))} /></Field>
        </div>
      </Card>
      <Card title="Kimlik">
        <Field label="Ad"><input className={inputCls} maxLength={40} value={a.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Alt başlık"><input className={inputCls} maxLength={60} value={a.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></Field>
        <UrlWithThumb label="Logo URL" value={a.logoUrl} onChange={(v) => set('logoUrl', v)} />
        <UrlWithThumb label="Avatar URL" value={a.avatarUrl} onChange={(v) => set('avatarUrl', v)} />
      </Card>
      <Card title="Davranış">
        <Field label="Açma düğmesi stili"><Segmented value={a.launcherStyle} onChange={(v) => set('launcherStyle', v)} options={[{ value: 'icon', label: 'Simge' }, { value: 'pill', label: 'Etiketli' }]} /></Field>
        <Toggle label="Mobilde tam ekran" checked={a.mobileFullscreen} onChange={(v) => set('mobileFullscreen', v)} />
        <Toggle label="'Glowytics ile' imzasını göster" checked={a.showPoweredBy} onChange={(v) => set('showPoweredBy', v)} />
      </Card>
    </>
  );
}

export function ToneTab({ config, update }) {
  const t = config.tone;
  const set = (k, v) => update('tone', { ...t, [k]: v });
  return (
    <Card title="Kişilik ve ton">
      <Field label="Persona" hint={`${t.persona.length}/800 — asistanın kim olduğu ve nasıl konuştuğu`}>
        <textarea rows={6} maxLength={800} className={inputCls} value={t.persona} onChange={(e) => set('persona', e.target.value)} />
      </Field>
      <Field label="Hitap"><Segmented value={t.address} onChange={(v) => set('address', v)} options={[{ value: 'sen', label: 'Sen' }, { value: 'siz', label: 'Siz' }]} /></Field>
      <Field label="Yanıt uzunluğu"><Segmented value={t.length} onChange={(v) => set('length', v)} options={[{ value: 'short', label: 'Kısa' }, { value: 'medium', label: 'Orta' }]} /></Field>
      <Toggle label="Emoji kullan" checked={t.emoji} onChange={(v) => set('emoji', v)} />
    </Card>
  );
}

export function WelcomeTab({ config, update }) {
  const w = config.welcome;
  const set = (k, v) => update('welcome', { ...w, [k]: v });
  return (
    <>
      <Card title="Karşılama mesajları" hint="1-3 mesaj, sırayla gösterilir">
        <StringListEditor items={w.messages} onChange={(v) => set('messages', v)} min={1} max={3} multiline sortable addLabel="Mesaj ekle" />
      </Card>
      <Card title="Hızlı yanıtlar" hint="2-6 düğme, ziyaretçi tek tıkla sorar">
        <StringListEditor items={w.quickReplies} onChange={(v) => set('quickReplies', v)} min={2} max={6} placeholder="Örn. Hediye önerir misin?" addLabel="Yanıt ekle" />
      </Card>
    </>
  );
}

export function BehaviorTab({ config, update }) {
  const b = config.behavior; const c = config.consent; const h = config.handoff;
  const setB = (k, v) => update('behavior', { ...b, [k]: v });
  const setC = (k, v) => update('consent', { ...c, [k]: v });
  return (
    <>
      <Card title="Kurallar" hint="Asistanın uyması gereken sabit kurallar (en fazla 20)">
        <StringListEditor items={b.rules} onChange={(v) => setB('rules', v)} max={20} multiline addLabel="Kural ekle" />
      </Card>
      <Card title="Yasak konular"><StringListEditor items={b.forbiddenTopics} onChange={(v) => setB('forbiddenTopics', v)} max={20} placeholder="Örn. siyaset" addLabel="Konu ekle" /></Card>
      <Card title="İnsana devir tetikleyicileri" hint="Bu ifadeler geçince asistan lead formu açar">
        <StringListEditor items={b.handoffTriggers} onChange={(v) => setB('handoffTriggers', v)} max={20} placeholder="Örn. şikayet" addLabel="Tetikleyici ekle" />
      </Card>
      <Card title="İzinli link alan adları" hint="Asistan yalnız bu alan adlarına link verebilir">
        <StringListEditor items={b.allowedLinkDomains} onChange={(v) => setB('allowedLinkDomains', v)} max={10} placeholder="hosgelsin.com" addLabel="Alan adı ekle" />
      </Card>
      <Card title="Çalışma saatleri">
        <input className={inputCls} maxLength={120} value={b.businessHours} placeholder="Hafta içi 09:00-18:00" onChange={(e) => setB('businessHours', e.target.value)} />
      </Card>
      <Card title="Onay metinleri (KVKK / İYS)">
        <Field label="KVKK aydınlatma URL"><input className={inputCls} value={c.kvkkUrl} placeholder="https://..." onChange={(e) => setC('kvkkUrl', e.target.value)} /></Field>
        <Field label="KVKK metni"><textarea rows={2} maxLength={300} className={inputCls} value={c.kvkkText} onChange={(e) => setC('kvkkText', e.target.value)} /></Field>
        <Field label="İYS (ticari ileti) metni"><textarea rows={2} maxLength={300} className={inputCls} value={c.iysText} onChange={(e) => setC('iysText', e.target.value)} /></Field>
      </Card>
      <Card title="Devir bildirimi" hint="Yeni lead geldiğinde mail gidecek adresler (en fazla 5)">
        <StringListEditor items={h.notifyEmails} onChange={(v) => update('handoff', { ...h, notifyEmails: v })} max={5} placeholder="ad@firma.com" addLabel="E-posta ekle" />
      </Card>
    </>
  );
}
